import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { appIdentifiers as appIdentifiersClient } from '../../ajax_clients'
import Spinner from './spinner'

export interface AppSearchResult {
  name: string
  bundleIdentifier: string
  type?: string
  iconUrl?: string
  added?: boolean
  supportedDevices?: string[]
}

const OS_TYPE_LABELS: Record<string, string> = {
  ios: 'iOS',
  macos: 'macOS',
  tvos: 'tvOS',
  visionos: 'visionOS',
}

function osTypeLabel(os: string): string {
  return OS_TYPE_LABELS[os] || os
}

export function privacyAppIdentity(bundleIdentifier: string, designatedRequirement = ''): string {
  const bundle = bundleIdentifier.trim().toLowerCase()
  const requirement = designatedRequirement.trim()
  if (!requirement) {
    return bundle
  }

  return `${bundle} {${requirement}}`
}

interface AddAppsModalProps {
  open: boolean
  onClose: () => void
  onAddApp: (app: AppSearchResult, designatedRequirement?: string) => void
  existingBundleIds: string[]
  queryType: string
  countryCodes?: [string, string][]
  appleStoreCountryCode?: string
  titleId?: string
  showDesignatedRequirement?: boolean
  designatedRequirementHelpText?: string
  existingAppIdentities?: string[]
}

function AddAppsModal(props: AddAppsModalProps) {
  const {
    open,
    onClose,
    onAddApp,
    existingBundleIds,
    queryType,
    countryCodes = [],
    appleStoreCountryCode = 'US',
    titleId = 'add-apps-modal',
    showDesignatedRequirement = false,
    designatedRequirementHelpText,
    existingAppIdentities = [],
  } = props

  const [searchTerm, setSearchTerm] = useState('')
  const [designatedRequirement, setDesignatedRequirement] = useState('')
  const [countryCode, setCountryCode] = useState(appleStoreCountryCode)
  const [searchResults, setSearchResults] = useState<AppSearchResult[]>([])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [noResults, setNoResults] = useState(false)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latestSearchRequestIdRef = useRef(0)
  const searchInputRef = useRef<HTMLInputElement>(null)

  const countryCodeOptions = useMemo(
    () => countryCodes.map(([label, value]) => ({ label, value })),
    [countryCodes]
  )

  const resetSearchState = useCallback(() => {
    latestSearchRequestIdRef.current += 1
    setSearchTerm('')
    setDesignatedRequirement('')
    setCountryCode(appleStoreCountryCode)
    setSearchResults([])
    setSearchError(null)
    setNoResults(false)
    setSearching(false)
  }, [appleStoreCountryCode])

  useEffect(() => {
    if (!open) {
      resetSearchState()
      return
    }

    searchInputRef.current?.focus()
  }, [open, resetSearchState])

  useEffect(() => () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
  }, [])

  const runSearch = useCallback((term: string, selectedCountryCode: string = countryCode) => {
    const requestId = latestSearchRequestIdRef.current + 1
    latestSearchRequestIdRef.current = requestId

    if (!term.trim()) {
      setSearchResults([])
      setSearchError(null)
      setNoResults(false)
      setSearching(false)
      return
    }

    setSearching(true)
    setSearchError(null)
    setNoResults(false)
    setSearchResults([])
    appIdentifiersClient.search(term, existingBundleIds, queryType, selectedCountryCode)
      .then((response: { searchResults: AppSearchResult[] }) => {
        if (requestId !== latestSearchRequestIdRef.current) {
          return
        }

        const results = response.searchResults || []
        setSearchResults(results)
        setNoResults(results.length === 0)
        setSearching(false)
      })
      .catch((error: { responseJSON?: { error?: { message?: string } } }) => {
        if (requestId !== latestSearchRequestIdRef.current) {
          return
        }

        setSearchResults([])
        setNoResults(false)
        setSearchError(error.responseJSON?.error?.message || 'Search failed. Please try again.')
        setSearching(false)
      })
  }, [existingBundleIds, queryType])

  const scheduleSearch = useCallback((term: string, selectedCountryCode: string = countryCode) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(() => {
      runSearch(term, selectedCountryCode)
    }, 500)
  }, [countryCode, runSearch])

  const onSearchInput = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const term = event.target.value
    setSearchTerm(term)
    scheduleSearch(term)
  }, [scheduleSearch])

  const onCountryChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCountryCode = event.target.value
    setCountryCode(selectedCountryCode)
    scheduleSearch(searchTerm, selectedCountryCode)
  }, [scheduleSearch, searchTerm])

  const onModalKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
    }
  }, [onClose])

  const onSearchKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    runSearch(searchTerm)
  }, [runSearch, searchTerm])

  const appResultAdded = useCallback((searchResult: AppSearchResult) => {
    if (showDesignatedRequirement) {
      return existingAppIdentities.includes(
        privacyAppIdentity(searchResult.bundleIdentifier, designatedRequirement)
      )
    }

    return Boolean(searchResult.added) || existingBundleIds.includes(searchResult.bundleIdentifier)
  }, [designatedRequirement, existingAppIdentities, existingBundleIds, showDesignatedRequirement])

  const handleAddApp = useCallback((searchResult: AppSearchResult) => {
    if (appResultAdded(searchResult)) {
      return
    }

    onAddApp(searchResult, designatedRequirement.trim())
  }, [appResultAdded, designatedRequirement, onAddApp])

  if (!open) {
    return null
  }

  return (
    <>
      <div
        className="modal fade in"
        style={{ display: 'block' }}
        tabIndex={-1}
        role="dialog"
        aria-labelledby={titleId}
        aria-modal="true"
        onKeyDown={onModalKeyDown}
      >
        <div className="modal-dialog add-apps-modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title" id={titleId}>Add Apps</h4>
            </div>
            <div className="modal-body add-app-modal">
              <div className="simple_form form-horizontal">
                <div className="form-group">
                  <label htmlFor="app-search" className="col-sm-3 control-label string">Search terms</label>
                  <div className="col-sm-9">
                    <input
                      ref={searchInputRef}
                      id="app-search"
                      type="text"
                      className="form-control"
                      autoComplete="off"
                      placeholder="Specify a bundle identifier or search by app name"
                      value={searchTerm}
                      onChange={onSearchInput}
                      onKeyDown={onSearchKeyDown}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="country" className="col-sm-3 control-label string">Store</label>
                  <div className="col-sm-9">
                    <select
                      id="country"
                      className="form-control select"
                      autoComplete="off"
                      value={countryCode}
                      onChange={onCountryChange}
                    >
                      {countryCodeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {showDesignatedRequirement ? (
                  <div className="form-group">
                    <label htmlFor="designated-requirement" className="col-sm-3 control-label string designated-requirement-label">
                      Designated Requirement
                    </label>
                    <div className="col-sm-9">
                      <input
                        id="designated-requirement"
                        type="text"
                        className="form-control"
                        autoComplete="off"
                        value={designatedRequirement}
                        onChange={(event) => setDesignatedRequirement(event.target.value)}
                      />
                      {designatedRequirementHelpText ? (
                        <span className="help-block">{designatedRequirementHelpText}</span>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="results">
                {!searching && noResults ? <div className="alert alert-warning">No results found</div> : null}
                {!searching && searchError ? (
                  <div className="alert alert-danger">
                    <strong>Error:</strong> {searchError}
                  </div>
                ) : null}
                <table className="table table-condensed table-striped">
                  <tbody>
                    {!searching && searchResults.map((searchResult) => {
                      const added = appResultAdded(searchResult)

                      return (
                        <tr key={searchResult.bundleIdentifier}>
                          <td className="icon">
                            {searchResult.iconUrl ? <img src={searchResult.iconUrl} alt="" /> : null}
                          </td>
                          <td className="name">{searchResult.name}</td>
                          <td className="os-types">
                            {(searchResult.supportedDevices || []).map((os) => (
                              <span
                                key={os}
                                className="label label-info platform-support-label"
                              >
                                {osTypeLabel(os)}
                              </span>
                            ))}
                          </td>
                          <td className="type">{searchResult.type}</td>
                          <td className="bundle-id">{searchResult.bundleIdentifier}</td>
                          <td className="action">
                            <span
                              role="button"
                              tabIndex={added ? -1 : 0}
                              className={`btn btn-default btn-xs btn-block add-app${added ? ' disabled' : ''}`}
                              onClick={() => handleAddApp(searchResult)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault()
                                  handleAddApp(searchResult)
                                }
                              }}
                            >
                              {added ? 'added' : 'add'}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                <div className="spinner-container" style={{ display: searching ? 'block' : 'none' }}>
                  <Spinner color="#333" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-default" onClick={onClose}>Close</button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade in" onClick={onClose} />
    </>
  )
}

export default AddAppsModal
